import type { RealBetEvent } from "@/features/common/socket/types";
import { makeAutoObservable } from "mobx";

class PendingBetsStore {
  bets: RealBetEvent[] = [];
  uiKeysByBetId = new Map<string, string>();
  /** Confirmed active bets count from server, kept in sync by useBetTracker. */
  confirmedActiveBetsCount = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setConfirmedActiveBetsCount(n: number) {
    this.confirmedActiveBetsCount = n;
  }

  add(bet: RealBetEvent) {
    if (this.bets.some((b) => b.betId === bet.betId)) return;
    const uiKey = bet.uiKey ?? bet.betId;
    this.rememberUiKey(bet.betId, uiKey);
    this.bets.push({ ...bet, uiKey });
  }

  remove(betId: string) {
    this.bets = this.bets.filter((b) => b.betId !== betId);
  }

  update(tempId: string, realBetId: string, updates?: Partial<RealBetEvent>) {
    const idx = this.bets.findIndex((b) => b.betId === tempId || b.betId === realBetId);
    if (idx !== -1) {
      const pendingBet = this.bets[idx];
      if (!pendingBet) return;

      const uiKey = pendingBet.uiKey ?? this.uiKeysByBetId.get(tempId) ?? tempId;
      this.rememberUiKey(realBetId, uiKey);
      const alreadyConfirmed = !pendingBet.betId.startsWith("optimistic-");
      this.bets[idx] = {
        ...pendingBet,
        betId: realBetId,
        uiKey,
        ...(alreadyConfirmed ? {} : updates),
      } as RealBetEvent;
    }
  }

  confirmAccepted(realBetId: string, accepted: Partial<RealBetEvent>): RealBetEvent | undefined {
    const acceptedFields = Object.fromEntries(
      Object.entries(accepted).filter(([, value]) => value !== undefined),
    ) as Partial<RealBetEvent>;
    const confirmed = this.bets.find((b) => b.betId === realBetId);
    if (confirmed) {
      return confirmed;
    }

    const acceptedAmount = accepted.amount == null ? null : Number(accepted.amount);
    const candidates = this.bets.filter(
      (b) =>
        b.betId.startsWith("optimistic-") &&
        (!accepted.pairSymbol || b.pairSymbol === accepted.pairSymbol) &&
        (!accepted.betType || b.betType === accepted.betType) &&
        (acceptedAmount === null || Number(b.amount) === acceptedAmount),
    );
    if (candidates.length === 0) return undefined;

    const targetMs = accepted.targetTime ? new Date(accepted.targetTime).getTime() : Number.NaN;
    const pendingBet = candidates.reduce((best, candidate) => {
      if (!Number.isFinite(targetMs)) return best;
      const bestTarget = best.targetTime ? new Date(best.targetTime).getTime() : Number.NaN;
      const candidateTarget = candidate.targetTime ? new Date(candidate.targetTime).getTime() : Number.NaN;
      return Math.abs(candidateTarget - targetMs) < Math.abs(bestTarget - targetMs) ? candidate : best;
    });
    const uiKey = pendingBet.uiKey ?? pendingBet.betId;
    this.rememberUiKey(realBetId, uiKey);
    Object.assign(pendingBet, acceptedFields, { betId: realBetId, uiKey });
    return pendingBet;
  }

  getUiKey(betId: string): string | undefined {
    return this.uiKeysByBetId.get(betId);
  }

  private rememberUiKey(betId: string, uiKey: string) {
    this.uiKeysByBetId.set(betId, uiKey);

    if (this.uiKeysByBetId.size > 100) {
      const oldestBetId = this.uiKeysByBetId.keys().next().value;
      if (oldestBetId) this.uiKeysByBetId.delete(oldestBetId);
    }
  }

  get totalActiveBetsCount(): number {
    return this.confirmedActiveBetsCount + this.bets.length;
  }

  get totalPendingAmount(): number {
    return this.bets.reduce((sum, bet) => sum + Number(bet.amount ?? 0), 0);
  }

  get optimisticPendingAmount(): number {
    return this.bets
      .filter((b) => b.betId.startsWith("optimistic-"))
      .reduce((sum, bet) => sum + Number(bet.amount ?? 0), 0);
  }
}

export const pendingBetsStore = new PendingBetsStore();
